import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Eyebrow,
  Heading,
  KeyValue,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Skeleton,
  StatusBadge,
  Textarea,
  type StatusKey,
} from '@insolech/ui';
import {
  ArrowLeft,
  Camera,
  Download,
  Eye,
  Hammer,
  Info,
  PackageCheck,
  Paperclip,
  Truck,
  Upload,
} from 'lucide-react';
import {
  providerQueries,
  orderQueries,
  type ProviderLineDetail,
  type ProviderLineFile,
} from '@insolech/sdk';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/features/auth/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  PrescriptionForm,
  type PrescriptionFormState,
  type PrescriptionModification,
} from '@/features/orders/wizard/PrescriptionForm';
import { SuggestModificationDialog } from './SuggestModificationDialog';

const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
  assigned_to_provider: 'assigned_to_provider',
  quality_check: 'quality_check',
};

/**
 * Per-line provider work surface. Phase 4d brief §4.
 *
 *  - Header: patient first name + short code, product, status, expected delivery
 *  - Prescription readout (read-only) opens the same drawer the podiatrist sees
 *  - STL/scan files with signed URL downloads (1h TTL)
 *  - Suggest-modification flow (multi-step opinionated dialog)
 *  - Status transition buttons gated by the state machine
 *  - Finished-product photo upload (required before in_delivery)
 *  - Provider observations editor
 *  - Read-only message thread with notice that providers cannot post
 */
export function ProviderLineDetailPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['provider', 'common']);
  const { lineId = '' } = useParams<{ lineId: string }>();
  const { user, userRole } = useAuth();
  const qc = useQueryClient();

  const detail = useQuery({
    ...providerQueries.providerLineDetailQuery(supabase, lineId),
    enabled: !!lineId,
  });
  const files = useQuery({
    ...providerQueries.providerLineFilesQuery(supabase, lineId),
    enabled: !!lineId,
  });
  // Read-only access to the order's message thread
  const messages = useQuery({
    ...orderQueries.orderMessagesQuery(supabase, detail.data?.order_id ?? ''),
    enabled: !!detail.data?.order_id,
  });

  // Realtime: when admin reassigns or the line transitions, refresh.
  React.useEffect(() => {
    if (!lineId) return;
    const channel = supabase
      .channel(`provider-line-${lineId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'order_lines', filter: `id=eq.${lineId}` },
        () => {
          void qc.invalidateQueries({ queryKey: providerQueries.providerKeys.lineDetail(lineId) });
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'order_line_files',
          filter: `order_line_id=eq.${lineId}`,
        },
        () => {
          void qc.invalidateQueries({ queryKey: providerQueries.providerKeys.lineFiles(lineId) });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [lineId, qc]);

  if (!lineId) {
    return (
      <AppShell>
        <main className="px-6 py-6 max-w-[1400px] mx-auto">
          <Alert tone="error" title="Line not found">
            Missing order line id.
          </Alert>
        </main>
      </AppShell>
    );
  }

  if (detail.isLoading) {
    return (
      <AppShell>
        <main className="px-6 py-6 max-w-[1400px] mx-auto space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </main>
      </AppShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AppShell>
        <main className="px-6 py-6 max-w-[1400px] mx-auto">
          <Alert tone="error" title="Could not load line">
            {String(detail.error ?? 'The line was not found or you are not authorised to view it.')}
          </Alert>
          <Button onClick={() => navigate('/provider/orders')} className="mt-4">
            Back to pipeline
          </Button>
        </main>
      </AppShell>
    );
  }

  const line = detail.data;
  const fileRows = (files.data ?? []) as ProviderLineFile[];
  const finishedPhotos = fileRows.filter((f) => f.file_kind === 'finished_product');
  const scanFiles = fileRows.filter((f) => f.file_kind !== 'finished_product');

  return (
    <AppShell>
      <main className="px-6 py-6 max-w-[1400px] mx-auto">
        <Button
          variant="ghost"
          size="sm"
          startIcon={<ArrowLeft className="h-4 w-4 rtl-flip" />}
          className="mb-4 -ms-2"
          onClick={() => navigate('/provider/orders')}
        >
          {t('common:action_back')}
        </Button>

        <ProviderLineHeader line={line} />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            <PrescriptionCard line={line} />
            <FilesCard
              title={t('provider:section_files')}
              files={scanFiles}
              loading={files.isLoading}
              emptyText="No scans or STL files attached to this line."
            />
            <FinishedPhotosCard
              line={line}
              files={finishedPhotos}
              loading={files.isLoading}
              currentUserId={user?.id ?? ''}
              onUploaded={() => {
                void qc.invalidateQueries({
                  queryKey: providerQueries.providerKeys.lineFiles(lineId),
                });
              }}
            />
            <ObservationsCard
              line={line}
              onSaved={() => {
                void qc.invalidateQueries({
                  queryKey: providerQueries.providerKeys.lineDetail(lineId),
                });
              }}
            />
            <MessagesReadOnlyCard messages={messages.data ?? []} loading={messages.isLoading} />
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <PatientCard line={line} />
            <ProductionActionsCard
              line={line}
              finishedPhotoCount={finishedPhotos.length}
              isAdmin={userRole === 'admin'}
              onChanged={() => {
                void qc.invalidateQueries({
                  queryKey: providerQueries.providerKeys.lineDetail(lineId),
                });
              }}
            />
            <SuggestModButton lineId={lineId} line={line} />
          </aside>
        </div>
      </main>
    </AppShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Header                                                                    */
/* -------------------------------------------------------------------------- */

function ProviderLineHeader({ line }: { line: ProviderLineDetail }) {
  const patientName =
    line.patient ? `${line.patient.first_name} ${line.patient.last_name}`.trim() : 'Patient';
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-border-subtle">
      <div className="flex items-start gap-4">
        <Avatar name={patientName} size="xl" />
        <div>
          <Eyebrow>
            {line.product?.name_en ?? formatLineType(line.line_type)} ·{' '}
            {line.order_number}
          </Eyebrow>
          <Heading as="h1" size="h3" className="mt-1">
            {patientName}
          </Heading>
          <div className="flex items-center gap-3 mt-2 text-sm text-text-secondary flex-wrap">
            {line.short_code ? (
              <span className="font-mono text-text-primary">{line.short_code}</span>
            ) : null}
            {line.assigned_at ? (
              <>
                <span className="opacity-60">·</span>
                <span>Assigned {formatDate(line.assigned_at)}</span>
              </>
            ) : null}
            {line.expected_delivery_date ? (
              <>
                <span className="opacity-60">·</span>
                <span>Expected {formatDate(line.expected_delivery_date)}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-3">
        <StatusBadge status={(STATUS_TO_KEY[line.status] ?? 'draft') as StatusKey} size="md" />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Prescription card — read-only drawer                                       */
/* -------------------------------------------------------------------------- */

function PrescriptionCard({ line }: { line: ProviderLineDetail }) {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation(['provider']);

  const value = React.useMemo<PrescriptionFormState>(() => {
    const cfg = (line.configuration ?? {}) as Record<string, unknown>;
    const sizeNode = cfg.size as
      | {
          left?: { length_mm?: number; width_mm?: number };
          right?: { length_mm?: number; width_mm?: number };
        }
      | undefined;
    const next: PrescriptionFormState = {
      line_type: (line.line_type as PrescriptionFormState['line_type']) ?? 'custom_insole',
      modifications: extractModifications(line),
    };
    if (line.product?.id) next.product_id = line.product.id;
    if (typeof cfg.material === 'string') next.material = cfg.material;
    if (typeof cfg.thickness_mm === 'number') next.thickness_mm = cfg.thickness_mm;
    if (typeof cfg.cover === 'string') next.cover = cfg.cover;
    if (typeof cfg.pathology === 'string') next.pathology = cfg.pathology;
    if (typeof cfg.observations === 'string') next.observations = cfg.observations;
    if (sizeNode?.left?.length_mm) next.size_left_length_mm = sizeNode.left.length_mm;
    if (sizeNode?.right?.length_mm) next.size_right_length_mm = sizeNode.right.length_mm;
    if (sizeNode?.left?.width_mm) next.size_left_width_mm = sizeNode.left.width_mm;
    if (sizeNode?.right?.width_mm) next.size_right_width_mm = sizeNode.right.width_mm;
    return next;
  }, [line]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('provider:section_prescription')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <KeyValue
          items={[
            { label: 'Product', value: line.product?.name_en ?? formatLineType(line.line_type) },
            {
              label: 'Material',
              value:
                ((line.configuration?.['material'] as string | undefined) ?? '-') as React.ReactNode,
            },
            {
              label: 'Modifications',
              value: `${value.modifications.length} configured`,
            },
            {
              label: 'Pathology',
              value:
                ((line.configuration?.['pathology'] as string | undefined) ?? '-') as React.ReactNode,
            },
          ]}
        />
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            startIcon={<Eye className="h-4 w-4" />}
            onClick={() => setOpen(true)}
            data-testid="provider-view-prescription"
          >
            {t('provider:action_view_prescription')}
          </Button>
        </div>
      </CardBody>

      <Modal open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <ModalContent size="xl">
          <ModalHeader>
            <ModalTitle>
              {t('provider:section_prescription')} · {line.order_number}
            </ModalTitle>
          </ModalHeader>
          <ModalBody className="max-h-[75vh] overflow-y-auto">
            <PrescriptionForm value={value} onChange={() => undefined} readOnly />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
}

function extractModifications(line: ProviderLineDetail): PrescriptionModification[] {
  const raw = (line.configuration as Record<string, unknown>)?.modifications;
  const fromLine = line.modifications as unknown;
  const candidate = Array.isArray(fromLine) && fromLine.length > 0 ? fromLine : raw;
  if (!Array.isArray(candidate)) return [];
  const out: PrescriptionModification[] = [];
  for (const m of candidate) {
    const item = m as Record<string, unknown>;
    const region = item.region as string | undefined;
    const modification_type = item.modification_type as string | undefined;
    if (!region || !modification_type) continue;
    const next: PrescriptionModification = {
      region: region as PrescriptionModification['region'],
      modification_type,
    };
    const intensity = item.intensity as PrescriptionModification['intensity'];
    if (intensity) next.intensity = intensity;
    if (item.parameters && typeof item.parameters === 'object') {
      next.parameters = item.parameters as Record<string, unknown>;
    }
    out.push(next);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/*  Files card                                                                 */
/* -------------------------------------------------------------------------- */

function FilesCard({
  title,
  files,
  loading,
  emptyText,
}: {
  title: string;
  files: ProviderLineFile[];
  loading: boolean;
  emptyText: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? (
          <Skeleton className="h-6 w-full" />
        ) : files.length === 0 ? (
          <p className="text-sm text-text-muted">{emptyText}</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {files.map((f) => (
              <FileRow key={f.id} file={f} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function FileRow({ file }: { file: ProviderLineFile }) {
  const [opening, setOpening] = React.useState(false);
  const onOpen = async () => {
    setOpening(true);
    const url = await orderQueries.getSignedUrlFor(supabase, file.storage_bucket, file.storage_path);
    setOpening(false);
    if (url) window.open(url, '_blank', 'noopener');
  };
  return (
    <li className="py-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-brand-50 text-brand-600 flex items-center justify-center">
        <Paperclip className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary truncate">{file.file_name}</p>
        <p className="text-xs text-text-muted">
          {file.file_kind} · {Math.round(file.file_size_bytes / 1024)} KB
        </p>
      </div>
      <Button variant="ghost" size="icon" aria-label="Download" onClick={onOpen} disabled={opening}>
        <Download className="h-4 w-4" />
      </Button>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/*  Finished-product photos card                                              */
/* -------------------------------------------------------------------------- */

function FinishedPhotosCard({
  line,
  files,
  loading,
  currentUserId,
  onUploaded,
}: {
  line: ProviderLineDetail;
  files: ProviderLineFile[];
  loading: boolean;
  currentUserId: string;
  onUploaded: () => void;
}) {
  const { t } = useTranslation(['provider']);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // We need the order/clinic to build the path. Pull from the line detail.
      const orderId = line.order_id;
      const clinicId = line.clinic?.id ?? '';
      if (!orderId || !clinicId) {
        throw new Error('Missing order or clinic context.');
      }
      await providerQueries.uploadFinishedPhoto(supabase, {
        orderLineId: line.order_line_id,
        orderId,
        clinicId,
        file,
        uploadedBy: currentUserId,
      });
    },
    onSuccess: () => {
      setError(null);
      onUploaded();
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : String(e));
    },
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    upload.mutate(f);
    e.target.value = '';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="h-4 w-4" />
          {t('provider:section_finished')}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        {error ? (
          <Alert tone="error" title="Upload failed">
            {error}
          </Alert>
        ) : null}
        {files.length === 0 ? (
          <Alert tone="info" title={t('provider:finished_required_warning')}>
            Upload a clear photo of each completed orthotic so the admin and podiatrist can confirm
            quality before delivery.
          </Alert>
        ) : (
          <p className="text-xs text-text-muted">
            {files.length === 1
              ? t('provider:finished_uploaded_count', { count: files.length })
              : t('provider:finished_uploaded_count_plural', { count: files.length })}
          </p>
        )}

        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : files.length === 0 ? null : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {files.map((f) => (
              <FinishedPhotoTile key={f.id} file={f} />
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onPick}
            data-testid="provider-finished-input"
          />
          <Button
            variant="primary"
            size="sm"
            startIcon={<Upload className="h-4 w-4" />}
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            data-testid="provider-finished-upload"
          >
            {upload.isPending ? 'Uploading…' : t('provider:action_upload_finished')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function FinishedPhotoTile({ file }: { file: ProviderLineFile }) {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancel = false;
    void orderQueries
      .getSignedUrlFor(supabase, file.storage_bucket, file.storage_path)
      .then((u) => {
        if (!cancel) setUrl(u);
      });
    return () => {
      cancel = true;
    };
  }, [file]);
  return (
    <a
      href={url ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="block aspect-square rounded-md overflow-hidden bg-sand-100 border border-border-subtle"
    >
      {url ? (
        <img
          src={url}
          alt={file.file_name}
          className="object-cover w-full h-full"
        />
      ) : (
        <div className="flex items-center justify-center h-full text-text-muted">
          <Camera className="h-6 w-6" />
        </div>
      )}
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/*  Observations editor                                                        */
/* -------------------------------------------------------------------------- */

function ObservationsCard({
  line,
  onSaved,
}: {
  line: ProviderLineDetail;
  onSaved: () => void;
}) {
  const { t } = useTranslation(['provider']);
  const [text, setText] = React.useState(line.provider_observations ?? '');
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setText(line.provider_observations ?? '');
  }, [line.provider_observations]);

  const save = useMutation({
    mutationFn: async () => {
      await providerQueries.providerUpdateObservations(supabase, line.order_line_id, text);
    },
    onSuccess: () => {
      setSaved(true);
      setError(null);
      onSaved();
      window.setTimeout(() => setSaved(false), 2_000);
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : String(e));
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('provider:section_observations')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <Textarea
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('provider:observations_placeholder')}
          data-testid="provider-observations-input"
        />
        {error ? (
          <p className="text-xs text-error-700">{error}</p>
        ) : saved ? (
          <p className="text-xs text-success-700">Saved.</p>
        ) : null}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending || text === (line.provider_observations ?? '')}
            data-testid="provider-observations-save"
          >
            {save.isPending ? 'Saving…' : t('provider:action_save_observations')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Messages thread (read-only)                                                */
/* -------------------------------------------------------------------------- */

function MessagesReadOnlyCard({
  messages,
  loading,
}: {
  messages: orderQueries.OrderMessageRow[];
  loading: boolean;
}) {
  const { t } = useTranslation(['provider']);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('provider:section_messages')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <Alert tone="info" title="Read-only">
          {t('provider:messages_provider_readonly_notice')}
        </Alert>
        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : messages.length === 0 ? (
          <p className="text-sm text-text-muted">No messages on this order yet.</p>
        ) : (
          <ul className="space-y-3 max-h-72 overflow-y-auto">
            {messages.map((m) => (
              <li key={m.id} className="flex gap-3">
                <Avatar name={m.sender?.full_name ?? m.sender_role} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-text-muted">
                    <strong>{m.sender?.full_name ?? capitalize(m.sender_role)}</strong> ·{' '}
                    {formatDate(m.created_at)}
                  </p>
                  <p className="text-sm text-text-primary mt-1 whitespace-pre-wrap">{m.message}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Patient card                                                               */
/* -------------------------------------------------------------------------- */

function PatientCard({ line }: { line: ProviderLineDetail }) {
  const patient = line.patient;
  const fullName = patient ? `${patient.first_name} ${patient.last_name}`.trim() : '-';
  return (
    <Card>
      <CardHeader>
        <CardTitle>Patient</CardTitle>
      </CardHeader>
      <CardBody>
        <div className="flex items-center gap-3 pb-4 border-b border-border-subtle">
          <Avatar name={fullName} size="md" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-text-primary truncate">{fullName}</p>
            <p className="text-xs text-text-muted font-mono">{line.short_code ?? '-'}</p>
          </div>
        </div>
        <KeyValue
          className="pt-4"
          items={[
            { label: 'Order', value: <span className="font-mono">{line.order_number}</span> },
            { label: 'Clinic', value: line.clinic?.name ?? '-' },
            { label: 'Podiatrist', value: line.podiatrist?.full_name ?? '-' },
            {
              label: 'Expected delivery',
              value: line.expected_delivery_date ? formatDate(line.expected_delivery_date) : '-',
            },
          ]}
        />
        <div className="mt-3">
          <Badge variant="muted">
            <Info className="h-3.5 w-3.5 me-1" />
            Last name masked for privacy
          </Badge>
        </div>
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Production action buttons                                                  */
/* -------------------------------------------------------------------------- */

function ProductionActionsCard({
  line,
  finishedPhotoCount,
  isAdmin,
  onChanged,
}: {
  line: ProviderLineDetail;
  finishedPhotoCount: number;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation(['provider']);
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<string | null>(null);

  const transition = async (to: string) => {
    setError(null);
    setPending(to);
    try {
      await providerQueries.providerTransitionLineStatus(supabase, line.order_line_id, to);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(null);
    }
  };

  // Provider-allowed transitions (mirror the state machine reference table).
  const allowed: Array<{
    to: string;
    labelKey: string;
    icon: React.ReactNode;
    requiresFinished?: boolean;
  }> = [];
  if (line.status === 'assigned_to_provider') {
    allowed.push({
      to: 'in_production',
      labelKey: 'provider:action_start_production',
      icon: <Hammer className="h-4 w-4" />,
    });
  }
  if (line.status === 'in_production') {
    allowed.push({
      to: 'quality_check',
      labelKey: 'provider:action_quality_check',
      icon: <PackageCheck className="h-4 w-4" />,
    });
  }
  if (line.status === 'quality_check') {
    allowed.push({
      to: 'in_delivery',
      labelKey: 'provider:action_mark_ready_to_ship',
      icon: <Truck className="h-4 w-4" />,
      requiresFinished: true,
    });
    // Allow rework path
    allowed.push({
      to: 'in_production',
      labelKey: 'provider:action_start_production',
      icon: <Hammer className="h-4 w-4" />,
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('provider:section_status_actions')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-text-muted">
          Current line status:{' '}
          <span className="font-mono text-text-primary">{line.status.replace(/_/g, ' ')}</span>
        </p>
        {error ? (
          <Alert tone="error" title="Could not update status">
            {error}
          </Alert>
        ) : null}
        {allowed.length === 0 ? (
          <p className="text-xs text-text-muted">
            {isAdmin
              ? 'No provider-side transitions available from this state. Use admin actions on the order detail page.'
              : 'No actions available, waiting on admin or further input.'}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {allowed.map((a) => {
              const blocked = a.requiresFinished && finishedPhotoCount === 0;
              return (
                <Button
                  key={a.to}
                  variant="primary"
                  size="sm"
                  onClick={() => transition(a.to)}
                  disabled={pending !== null || blocked}
                  startIcon={a.icon}
                  data-testid={`provider-transition-${a.to}`}
                >
                  {pending === a.to ? 'Updating…' : t(a.labelKey)}
                </Button>
              );
            })}
            {allowed.some((a) => a.requiresFinished) && finishedPhotoCount === 0 ? (
              <p className="text-xs text-warning-700">
                {t('provider:transition_error_finished_required')}
              </p>
            ) : null}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Suggest-modification button + dialog                                       */
/* -------------------------------------------------------------------------- */

function SuggestModButton({ lineId, line }: { lineId: string; line: ProviderLineDetail }) {
  const { t } = useTranslation(['provider']);
  const [open, setOpen] = React.useState(false);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Communicate</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-text-muted">
          Providers can suggest a prescription change. The podiatrist receives a notification and
          can accept, reject, or comment.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="provider-suggest-mod-open"
        >
          {t('provider:action_suggest_modification')}
        </Button>
      </CardBody>
      <SuggestModificationDialog
        open={open}
        onOpenChange={setOpen}
        lineId={lineId}
        line={line}
      />
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatLineType(s: string | undefined): string {
  if (!s) return '-';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
